import React, { useState, useEffect, useContext } from 'react';
import './Availability.css';
import { LanguageContext } from "../context/LanguageContext";

export default function AvailabilitySettings({
  objectId,
  onRefresh,
  setMessage,
  setActiveTab,
  availabilityMode,
  setAvailabilityMode
}) {
  const { texts } = useContext(LanguageContext);

  const [isSaving, setIsSaving] = useState(false);

  // ⭐ Merken, ob User bereits gewählt hat
  const [hasChosenMode, setHasChosenMode] = useState(false);

  // ⭐ Merken, ob Backend-Werte geladen wurden
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // ⭐ Modal steuern
  const [showInitialModal, setShowInitialModal] = useState(false);

  // ------------------------------------------------------------
  // 1) Prüfen, ob User bereits gewählt hat (localStorage)
  // ------------------------------------------------------------
  useEffect(() => {
    const saved = localStorage.getItem(`availabilityModeChosen:${objectId}`);
    if (saved === "true") {
      setHasChosenMode(true);
    }
  }, [objectId]);

  // ------------------------------------------------------------
  // 2) Backend-Settings laden (falls nicht extern geladen)
  // ------------------------------------------------------------
  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/availability/settings/${objectId}`
        );
        const data = await res.json();

        // Backend liefert: "manual" | "ical" | "none"
        setAvailabilityMode(data.availability_mode);
        setSettingsLoaded(true);
      } catch (err) {
        console.error("Fehler beim Laden der Settings:", err);
      }
    }

    load();
  }, [objectId, setAvailabilityMode]);

  // ------------------------------------------------------------
  // 3) Modal nur zeigen, wenn:
  //    - Backend ist geladen
  //    - availabilityMode === "none"
  //    - User hat noch NIE gewählt
  // ------------------------------------------------------------
  useEffect(() => {
    if (!settingsLoaded) return;

    if (availabilityMode === "none" && !hasChosenMode) {
      setShowInitialModal(true);
    } else {
      setShowInitialModal(false);
    }
  }, [availabilityMode, hasChosenMode, settingsLoaded]);

  // ------------------------------------------------------------
  // 4) Modus ändern
  // ------------------------------------------------------------
  const handleModeChange = async (newMode, skipWarning = false) => {
    if (newMode === availabilityMode) return;

    if (!skipWarning) {
      const confirmed = window.confirm(texts.availability.warningSwitch);
      if (!confirmed) return;
    }

    setIsSaving(true);

    await fetch(
      `${process.env.REACT_APP_API_URL}/api/availability/settings/${objectId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ availability_mode: newMode })
      }
    );

    // ⭐ dauerhaft merken
    setHasChosenMode(true);
    localStorage.setItem(`availabilityModeChosen:${objectId}`, "true");

    // ⭐ Tabs sofort aktualisieren
    setAvailabilityMode(newMode);

    setIsSaving(false);
    onRefresh();

    // Weiterleitung
    if (newMode === "ical") setActiveTab("ical");
    if (newMode === "manual") setActiveTab("manual");
  };

  // ------------------------------------------------------------
  // 5) Modal Rendering
  // ------------------------------------------------------------
  const renderInitialModal = () => {
    if (!showInitialModal) return null;

    return (
      <div className="availability-modal-backdrop">
        <div className="availability-modal">
          <h2>{texts.availability.modalTitle}</h2>
          <p>{texts.availability.modalText}</p>

          <div className="availability-modal-buttons">
            <button
              className="availability-modal-button"
              onClick={() => {
                setShowInitialModal(false);
                handleModeChange("ical", true);
              }}
            >
              {texts.availability.chooseIcal}
            </button>

            <button
              className="availability-modal-button"
              onClick={() => {
                setShowInitialModal(false);
                handleModeChange("manual", true);
              }}
            >
              {texts.availability.chooseManual}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------
  // 6) Render
  // ------------------------------------------------------------
  return (
    <div className="settings-tab">
      <h2>{texts.availability.settingsTitle}</h2>

      {renderInitialModal()}

      <div className="availability-mode-box">
        <h3>{texts.availability.modeTitle}</h3>

        <label>
          <input
            type="radio"
            name="mode"
            value="ical"
            checked={availabilityMode === "ical"}
            onChange={() => handleModeChange("ical")}
          />
          {texts.availability.modeIcal}
        </label>

        <label>
          <input
            type="radio"
            name="mode"
            value="manual"
            checked={availabilityMode === "manual"}
            onChange={() => handleModeChange("manual")}
          />
          {texts.availability.modeManual}
        </label>
      </div>

      {isSaving && <p>{texts.common.saving}</p>}
    </div>
  );
}
